import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';

/**
 * Health-budget surface (T-0104 / C20-114, E07-F04; the Budget domain's
 * tiles, spend curve and monthly plan editor from T-0243 / E22).
 *
 *   GET /api/v1/engagements/:engagement_id/health/budget[?include=series]
 *     → { original_contract, change_order_delta, revised_contract,
 *         authorised, actual_to_date, plan_to_date, projected_total,
 *         plan_months, plan_total, plan_difference, series?, … }
 *   PUT …/health/budget/plan  { months: [{ month: 'YYYY-MM', planned_amount }] }
 *     replaces the monthly plan; the months must add up to the authorised
 *     total to the cent, or the API answers 400 saying by how much (T-0240).
 *
 * Every figure the screen shows is the API's, in dollars: the authorised
 * ceiling, spend to date, the plan to date, the projection, the cumulative
 * series and the plan's difference. The browser does not add any of them
 * up. `pctVariance` (spend against the plan to date) is the one ratio,
 * kept from T-0226 so a plan of 0 reads "not applicable", never -100%.
 *
 * The plan editor's helpers (`budgetPlanDraft`, `planDraftTotals`) work on
 * what the lead types: a starting grid and the typed sum against the
 * authorised total, so the difference shows before saving. The API stays
 * the judge of the sum.
 */

// ===========================================================================
// Backend shape (subset relevant to the UI).
// ===========================================================================

const BudgetEntrySchema = z.object({
  id: z.string(),
  layer: z.enum(['change_order_delta', 'actual_spend']),
  amount: z.number(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  description: z.string().nullable(),
  origin: z.enum([
    'manually_authored',
    'ai_extracted_confirmed_as_is',
    'ai_extracted_edited_by_consultant',
  ]),
  original_ai_confidence: z.number().nullable(),
  ai_proposal_id: z.string().nullable(),
  evidence_link_id: z.string().nullable(),
  override_reason_tag: z.string().nullable(),
  override_rationale: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

const EvmSchema = z.object({
  ev: z.number(),
  ac: z.number(),
  pv: z.number(),
  bac: z.number(),
  cv: z.number(),
  sv: z.number(),
  cpi: z.number(),
  spi: z.number(),
  eac: z.number(),
  etc: z.number(),
});

const VarianceStateSchema = z.enum(['ok', 'warn', 'critical']);
export type VarianceStateBackend = z.infer<typeof VarianceStateSchema>;

/**
 * T-0129 (PR #154) opt-in monthly cumulative series. The composite GET
 * returns this when `?include=series` is set. Each point is `(month,
 * plan, actual, projected)` cumulative to end-of-month.
 */
const BudgetSeriesPointSchema = z.object({
  month: z.string(),
  plan: z.number(),
  actual: z.number(),
  projected: z.number(),
});

export const PLAN_CURVE_TYPES = ['linear', 'front_loaded', 'back_loaded', 'custom'] as const;
export type PlanCurveType = (typeof PLAN_CURVE_TYPES)[number];

const BudgetBackendSchema = z.object({
  budget_id: z.string(),
  engagement_id: z.string(),
  currency: z.string(),
  burn_window_months: z.number(),
  variance_warn_pct: z.number(),
  variance_critical_pct: z.number(),
  original_contract: z.number(),
  change_order_delta: z.number(),
  revised_contract: z.number(),
  actual_to_date: z.number(),
  variance: z.number(),
  variance_pct: z.number(),
  variance_state: VarianceStateSchema,
  burn_rate_per_month: z.number(),
  projected_total: z.number(),
  evm: EvmSchema,
  entries: z.array(BudgetEntrySchema),
  // T-0129 plan curve + series. plan_to_date is present on every response
  // (defaults to 0 when no curve is configured); series only when
  // ?include=series is set on the request.
  plan_to_date: z.number().optional(),
  series: z.array(BudgetSeriesPointSchema).optional(),
  contract_start_date: z.string().nullable().optional(),
  contract_end_date: z.string().nullable().optional(),
  plan_curve_type: z.enum(PLAN_CURVE_TYPES).optional(),
  // T-0240: the authorised ceiling (contract + approved change orders) and
  // the monthly plan with its sum and its difference to that ceiling.
  // Optional so an older API still parses.
  authorised: z.number().optional(),
  plan_months: z.array(z.object({ month: z.string(), planned_amount: z.number() })).optional(),
  plan_total: z.number().nullable().optional(),
  plan_difference: z.number().nullable().optional(),
});

export type BudgetBackend = z.infer<typeof BudgetBackendSchema>;

// ===========================================================================
// The shape the Budget domain reads (T-0243): the API's numbers in dollars,
// renamed; nothing is added up here.
// ===========================================================================

export interface BudgetSeriesMonth {
  /** Calendar month, "2026-04". */
  month: string;
  /** Cumulative to the end of the month, dollars. */
  plan: number;
  actual: number;
  projected: number;
}

export interface BudgetPlanMonth {
  /** Calendar month, "2026-04". */
  month: string;
  plannedAmount: number;
}

export interface EngagementBudget {
  currency: string;
  /** The contract value the baseline holds. */
  originalContract: number;
  /** Net of the approved change orders. */
  changeOrderDelta: number;
  /** The authorised ceiling: contract plus approved change orders. */
  authorised: number;
  actualToDate: number;
  /**
   * The plan curve at today; 0 when no curve is configured or the contract
   * has not started (the variance against it is then "not applicable").
   */
  planToDate: number;
  /** Whether the baseline has a plan: contract start and end, or a monthly plan. */
  planConfigured: boolean;
  /** Actual to date plus the plan still to spend (E22 design §2). */
  projectedTotal: number;
  /** Monthly cumulative points; [] unless asked for with `includeSeries`. */
  series: BudgetSeriesMonth[];
  contractStartDate: string | null;
  contractEndDate: string | null;
  planCurveType: PlanCurveType;
  /** The monthly plan as entered, oldest first; [] when there is none. */
  planMonths: BudgetPlanMonth[];
  /** The monthly plan's sum; null without one. */
  planTotal: number | null;
  /** Plan total minus the authorised ceiling; null without a monthly plan. */
  planDifference: number | null;
}

function hydrate(backend: BudgetBackend): EngagementBudget {
  const planMonths = (backend.plan_months ?? []).map((m) => ({
    // The API writes "2026-04"; a first-of-month date reads the same.
    month: m.month.slice(0, 7),
    plannedAmount: m.planned_amount,
  }));
  return {
    currency: backend.currency,
    originalContract: backend.original_contract,
    changeOrderDelta: backend.change_order_delta,
    // An API before T-0240 has no `authorised`; its revised contract is the same figure.
    authorised: backend.authorised ?? backend.revised_contract,
    actualToDate: backend.actual_to_date,
    planToDate: backend.plan_to_date ?? 0,
    planConfigured:
      planMonths.length > 0 ||
      (Boolean(backend.contract_start_date) && Boolean(backend.contract_end_date)),
    projectedTotal: backend.projected_total,
    series: (backend.series ?? []).map((p) => ({
      month: p.month.slice(0, 7),
      plan: p.plan,
      actual: p.actual,
      projected: p.projected,
    })),
    contractStartDate: backend.contract_start_date ?? null,
    contractEndDate: backend.contract_end_date ?? null,
    planCurveType: backend.plan_curve_type ?? 'linear',
    planMonths,
    planTotal: backend.plan_total ?? null,
    planDifference: backend.plan_difference ?? null,
  };
}

// ===========================================================================
// Hook.
// ===========================================================================

export interface GetEngagementBudgetOpts {
  /** When `true`, asks the backend for the monthly cumulative `series`
   *  array (T-0129). The line-chart consumer wants this; KPI-only views
   *  can skip it to avoid the extra derivation cost. */
  includeSeries?: boolean;
}

export function getEngagementBudget(
  engagementId: string,
  opts: GetEngagementBudgetOpts = {},
): Promise<EngagementBudget> {
  const query: Record<string, string | undefined> = {};
  if (opts.includeSeries) query.include = 'series';
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/budget`,
    BudgetBackendSchema,
    { query },
  ).then(hydrate);
}

export function useEngagementBudgetQuery(
  engagementId: string | undefined,
  opts: GetEngagementBudgetOpts = {},
) {
  return useQuery({
    queryKey: ['health', 'budget', engagementId, opts.includeSeries ?? false] as const,
    // "No budget baseline yet" is an expected state, not a failure: the
    // API answers 404 `No budget exists for this engagement.` Resolve it
    // to `null` so screens render their setup / empty state. Any other
    // 404 (engagement missing or not accessible) still surfaces as an error.
    queryFn: async (): Promise<EngagementBudget | null> => {
      try {
        return await getEngagementBudget(engagementId!, opts);
      } catch (e) {
        if (isNoBudgetYet(e)) return null;
        throw e;
      }
    },
    enabled: Boolean(engagementId),
    staleTime: 60_000,
  });
}

/** True for the API's "budget baseline not created yet" 404. */
export function isNoBudgetYet(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && e.message.startsWith('No budget exists');
}

// ===========================================================================
// Variance helpers — pure code, never AI-derived (R6).
// ===========================================================================

/**
 * Percent variance of `actual` against `baseline`.
 *
 * Returns `null` ("not applicable") when the baseline is 0: with no plan
 * to compare against there is no percentage, and inventing one produced
 * the "-100.0% vs. plan" a fresh budget used to show (C2-25). Callers
 * render `null` as "—" with the reason ("no plan yet").
 */
export function pctVariance(actual: number, baseline: number): number | null {
  if (baseline === 0) return null;
  return ((actual - baseline) / baseline) * 100;
}

export type VariancePosture = 'healthy' | 'at-risk' | 'critical' | 'unassessed';

/**
 * Bucket a variance percentage into the posture vocabulary. The
 * thresholds (>3% → at-risk, >7% → critical) match the prototype's
 * `variancePct` chip rule for the budget tracker. A not-applicable
 * variance has no posture: `unassessed`.
 */
export function variancePosture(pct: number | null): VariancePosture {
  if (pct === null) return 'unassessed';
  if (pct > 7) return 'critical';
  if (pct > 3) return 'at-risk';
  return 'healthy';
}

/** "+4.2%" / "-1.0%", or "—" when the variance is not applicable. */
export function formatVariancePct(pct: number | null): string {
  if (pct === null) return '\u2014';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// ===========================================================================
// Form helpers — pure, unit-tested; the cards in components/health/budget-forms
// render what these return.
// ===========================================================================

/** The calendar month `today` falls in, as the ISO date range the entry form
 *  defaults to for actual spend (first day → last day). */
export function currentMonthPeriod(today: Date = new Date()): {
  period_start: string;
  period_end: string;
} {
  const y = today.getFullYear();
  const m = today.getMonth();
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { period_start: iso(new Date(y, m, 1)), period_end: iso(new Date(y, m + 1, 0)) };
}

export interface BudgetEntryFormValues {
  layer: 'change_order_delta' | 'actual_spend';
  amount: string;
  period_start: string;
  period_end: string;
}

export interface BudgetEntryFormErrors {
  amount?: string;
  period_start?: string;
  period_end?: string;
}

/**
 * Client-side mirror of the entry rules in apps/api/src/services/health-budget.ts
 * (`actual_spend entries require period_start and period_end`, start on or
 * before end, actual spend non-negative) so the form can say what is wrong
 * next to the field instead of bouncing a 400 (C2-09). The API still
 * validates; this only decides what the form shows and whether it submits.
 */
export function validateBudgetEntryForm(v: BudgetEntryFormValues): BudgetEntryFormErrors {
  const errors: BudgetEntryFormErrors = {};
  const amount = Number(v.amount);
  if (v.amount.trim() === '' || !Number.isFinite(amount) || amount === 0) {
    errors.amount = 'Enter a non-zero amount.';
  } else if (v.layer === 'actual_spend' && amount < 0) {
    errors.amount = "Actual spend can't be negative.";
  }
  if (v.layer === 'actual_spend') {
    if (!v.period_start) errors.period_start = 'Period start is required for actual spend.';
    if (!v.period_end) errors.period_end = 'Period end is required for actual spend.';
  }
  if (v.period_start && v.period_end && v.period_start > v.period_end) {
    errors.period_start = 'Period start must be on or before period end.';
  }
  return errors;
}

/**
 * Dollars in an approved `contract_value` fact, or null when the text holds
 * no usable amount. The fact is captured "as written with its currency"
 * ("$4,250,000.00", "USD 4.25 million", "4,250,000"), so this mirrors the
 * amount rule in packages/ai-contract-analysis/src/facts.ts: first number,
 * thousands separators, optional decimals, optional million / k unit. A
 * contract value is never negative, so a negative reading is rejected.
 */
export function contractValueToDollars(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = /(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s*(million|m|k|thousand)?\b/i.exec(text);
  if (!m) return null;
  let n = Number(`${m[1]!.replace(/,/g, '')}${m[2] ? `.${m[2]}` : ''}`);
  const unit = (m[3] ?? '').toLowerCase();
  if (unit === 'million' || unit === 'm') n *= 1_000_000;
  if (unit === 'thousand' || unit === 'k') n *= 1_000;
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// ===========================================================================
// Budget writes (T-0074 backends — client wiring added in Phase 1)
// ===========================================================================

export interface CreateBudgetBody {
  original_contract_amount: number;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
}

export interface CreateBudgetEntryBody {
  layer: 'change_order_delta' | 'actual_spend';
  amount: number;
  description?: string | null;
  period_start?: string | null;
  period_end?: string | null;
}

const BudgetWriteResultSchema = z.object({ id: z.string().optional() }).passthrough();

export function useCreateBudgetMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBudgetBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/budget`,
        BudgetWriteResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'budget', engagementId] });
      // The overview's Budget tile reads the rollup off the engagement detail.
      void qc.invalidateQueries({ queryKey: ['engagements', 'detail', engagementId] });
    },
  });
}

export function useCreateBudgetEntryMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBudgetEntryBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/budget/entries`,
        BudgetWriteResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'budget', engagementId] });
      // The overview's Budget tile reads the rollup off the engagement detail.
      void qc.invalidateQueries({ queryKey: ['engagements', 'detail', engagementId] });
    },
  });
}

// ===========================================================================
// Monthly spend plan (T-0243 editor over T-0240's PUT …/health/budget/plan)
// ===========================================================================

/** One month of the plan editor, the amount as the lead typed it (dollars). */
export interface PlanDraftRow {
  /** Calendar month, "2026-04". */
  month: string;
  amount: string;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function monthKey(year: number, monthIndex: number): string {
  const d = new Date(year, monthIndex, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function parseMonth(key: string): { year: number; index: number } {
  return { year: Number(key.slice(0, 4)), index: Number(key.slice(5, 7)) - 1 };
}

/** "2026-04" → "Apr 2026". */
export function planMonthLabel(month: string): string {
  const { year, index } = parseMonth(month);
  return `${MONTH_NAMES[index] ?? month} ${year}`;
}

/** "2026-04" → "Apr", the spend curve's axis label. */
export function shortMonthLabel(month: string): string {
  return MONTH_NAMES[parseMonth(month).index] ?? month;
}

const MAX_PLAN_MONTHS = 240;

/**
 * The grid the plan editor opens with. The saved monthly plan when there is
 * one; otherwise the months from the contract start to its end (twelve from
 * the start, or from this month, when a date is missing), each the
 * authorised total split evenly to the cent with the remainder on the last
 * month: the linear curve, as a starting point the lead edits.
 */
export function budgetPlanDraft(
  b: Pick<EngagementBudget, 'planMonths' | 'authorised' | 'contractStartDate' | 'contractEndDate'>,
  today: Date,
): PlanDraftRow[] {
  if (b.planMonths.length > 0) {
    return b.planMonths.map((m) => ({ month: m.month, amount: m.plannedAmount.toFixed(2) }));
  }
  const start = b.contractStartDate
    ? parseMonth(b.contractStartDate.slice(0, 7))
    : { year: today.getFullYear(), index: today.getMonth() };
  let count = 12;
  if (b.contractStartDate && b.contractEndDate) {
    const end = parseMonth(b.contractEndDate.slice(0, 7));
    count = (end.year - start.year) * 12 + (end.index - start.index) + 1;
  }
  count = Math.min(MAX_PLAN_MONTHS, Math.max(1, count));
  const cents = Math.round(b.authorised * 100);
  const each = Math.floor(cents / count);
  return Array.from({ length: count }, (_, i) => {
    const share = i === count - 1 ? cents - each * (count - 1) : each;
    return { month: monthKey(start.year, start.index + i), amount: (share / 100).toFixed(2) };
  });
}

/** A typed amount in cents; null when it is not a non-negative amount. */
export function amountCents(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/**
 * The typed plan against the authorised total, in cents so the sum is
 * exact: the plan total, its difference to the authorised total (negative =
 * under) and whether every month holds an amount. Form arithmetic on what
 * the lead typed; the API checks the sum again on save.
 */
export function planDraftTotals(
  rows: ReadonlyArray<PlanDraftRow>,
  authorised: number,
): { total: number; difference: number; valid: boolean } {
  let cents = 0;
  let valid = rows.length > 0;
  for (const r of rows) {
    const c = amountCents(r.amount);
    if (c === null) valid = false;
    else cents += c;
  }
  return {
    total: cents / 100,
    difference: (cents - Math.round(authorised * 100)) / 100,
    valid,
  };
}

/** "$4,430,000.00": the plan editor's figures, to the cent as the API words its refusal. */
export function formatAmount(n: number): string {
  return `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export interface PutBudgetPlanBody {
  months: Array<{ month: string; planned_amount: number }>;
}

/** The body the editor sends: every month, its amount in dollars to the cent. */
export function planBody(rows: ReadonlyArray<PlanDraftRow>): PutBudgetPlanBody {
  return {
    months: rows.map((r) => ({
      month: r.month,
      planned_amount: (amountCents(r.amount) ?? 0) / 100,
    })),
  };
}

/**
 * Replace the monthly plan (lead). The API refuses a plan that does not add
 * up to the authorised total with a 400 that says by how much; the editor
 * shows that message as it is.
 */
export function usePutBudgetPlanMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PutBudgetPlanBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/budget/plan`,
        BudgetWriteResultSchema,
        {
          method: 'PUT',
          body,
          idempotencyKey:
            typeof globalThis.crypto?.randomUUID === 'function'
              ? globalThis.crypto.randomUUID()
              : `budget-plan-${Date.now()}`,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'budget', engagementId] });
      void qc.invalidateQueries({ queryKey: ['engagements', 'detail', engagementId] });
    },
  });
}
